<script lang="ts">
  import { t } from '$i18n/index';
  import type { MessageKey } from '$i18n/index';
  import { navigate } from '$ui/router';
  import { app } from '$ui/app-state.svelte';
  import { requestPersistentStorage } from '$app/bootstrap';
  import { ONBOARDING_LIMITS } from '$domain/constants';
  import type { PlanContext } from '$domain/context-match';
  import {
    clearDraft,
    completeOnboarding,
    emptyDraft,
    loadDraft,
    newBehaviorId,
    newDirectionId,
    newPlanId,
    saveDraft,
  } from '$app/onboarding-service';
  import type { DraftPlan, OnboardingDraft } from '$app/onboarding-service';
  import {
    PLAN_CONTEXTS,
    SUGGESTED_BEHAVIORS,
    SUGGESTED_DIRECTIONS,
    contextLabelKey,
    isStep,
    nextStep,
    previousStep,
  } from '$ui/components/onboarding/steps';
  import type { Step } from '$ui/components/onboarding/steps';
  import Chip from '$ui/components/onboarding/Chip.svelte';
  import PlacementRitual from '$ui/components/onboarding/PlacementRitual.svelte';
  import { focusOnMount } from '$ui/focus';

  /**
   * The one long flow in the product (spec §6.1) — ten minutes, once.
   *
   * It is **abandonable and resumable**: the draft lives in the scratch store
   * and is written on every step change, so leaving costs nothing and coming
   * back lands where you left. Nothing here is an event until the very end;
   * a half-filled form is not a fact about someone's life.
   */

  let draft = $state<OnboardingDraft>(emptyDraft());
  let step = $state<Step>('intro');
  let loaded = $state(false);
  let saidAloud = $state<boolean | undefined>(undefined);
  let aloudIndex = $state(0);
  let customDirection = $state('');
  let customBehavior = $state('');
  let failed = $state(false);
  let finishing = $state(false);

  $effect(() => {
    void (async () => {
      const ctx = await app.context();
      const stored = await loadDraft(ctx);
      draft = stored;
      step = isStep(stored.step) ? stored.step : 'intro';
      loaded = true;
    })();
  });

  /**
   * `$state.snapshot`, not the state object itself.
   *
   * Svelte 5 hands out a reactive Proxy, and IndexedDB's structured clone
   * refuses to serialise one — every save threw `could not be cloned`, silently,
   * because nothing awaited the rejection. The flow looked fine and resumed
   * from nothing. Snapshot converts it to a plain object first.
   */
  async function persist() {
    const ctx = await app.context();
    await saveDraft(ctx, { ...$state.snapshot(draft), step });
  }

  async function go(to: Step) {
    step = to;
    await persist();
  }

  // ------------------------------------------------------------- layer 1
  const directionCount = $derived(draft.directions.length);

  /** Directions the user wrote themselves, shown alongside the suggestions. */
  const ownDirections = $derived(
    draft.directions.filter(
      (d) => !SUGGESTED_DIRECTIONS.some((s) => s.directionId === d.directionId),
    ),
  );

  const ownBehaviors = $derived(
    draft.targetBehaviors.filter(
      (b) => !SUGGESTED_BEHAVIORS.some((s) => s.behaviorId === b.behaviorId),
    ),
  );

  function toggleDirection(directionId: string, label: string) {
    const existing = draft.directions.find((d) => d.directionId === directionId);
    if (existing) {
      draft.directions = draft.directions.filter((d) => d.directionId !== directionId);
      const { [directionId]: _removed, ...rest } = draft.expressions;
      draft.expressions = rest;
      draft.plans = draft.plans.filter((p) => p.directionRef !== directionId);
      return;
    }
    if (directionCount >= ONBOARDING_LIMITS.maxDirections) return;
    draft.directions = [...draft.directions, { directionId, label }];
  }

  function addCustomDirection() {
    const label = customDirection.trim();
    if (!label || directionCount >= ONBOARDING_LIMITS.maxDirections) return;
    draft.directions = [...draft.directions, { directionId: newDirectionId(), label }];
    customDirection = '';
  }

  // ------------------------------------------------------------- layer 3
  function toggleBehavior(behaviorId: string, label: string) {
    const existing = draft.targetBehaviors.find((b) => b.behaviorId === behaviorId);
    if (existing) {
      draft.targetBehaviors = draft.targetBehaviors.filter((b) => b.behaviorId !== behaviorId);
      return;
    }
    if (draft.targetBehaviors.length >= ONBOARDING_LIMITS.maxTargetBehaviors) return;
    draft.targetBehaviors = [...draft.targetBehaviors, { behaviorId, label }];
  }

  function addCustomBehavior() {
    const label = customBehavior.trim();
    if (!label || draft.targetBehaviors.length >= ONBOARDING_LIMITS.maxTargetBehaviors) return;
    draft.targetBehaviors = [...draft.targetBehaviors, { behaviorId: newBehaviorId(), label }];
    customBehavior = '';
  }

  // --------------------------------------------------------------- plans
  /**
   * Seed one plan per direction, prefilled from what the user has already said.
   *
   * Ten minutes is the budget for the whole flow, and typing three if-then
   * sentences on a phone does not fit in it. Prefilling from their own words —
   * their behaviour as the trigger, their expression as the action — turns
   * writing into editing, which is both faster and more likely to produce a
   * sentence they recognise as theirs.
   */
  function seedPlans() {
    if (draft.plans.length > 0) return;
    const behaviors = draft.targetBehaviors;
    const directions = draft.directions;
    if (directions.length === 0) return;

    // At least the minimum, even when someone named fewer directions than that.
    // Cycling their own directions and behaviours is better than asking them to
    // invent a third: spec §6.1 wants 3-6 plans, and a blank card someone has to
    // fill from nothing is where a ten-minute flow becomes a twenty-minute one.
    const count = Math.max(directions.length, ONBOARDING_LIMITS.minPlans);

    draft.plans = Array.from({ length: count }, (_unused, index) => {
      const direction = directions[index % directions.length];
      const behavior = behaviors[index % Math.max(behaviors.length, 1)];
      return {
        planId: newPlanId(),
        trigger: behavior ? `I catch myself ${behavior.label.toLowerCase()}` : '',
        action: direction ? (draft.expressions[direction.directionId] ?? '') : '',
        // Default to "any time" rather than guessing an hour. We do not know
        // when someone's pull happens, and a plan pinned to a bucket we picked
        // for them is a plan that mostly does not appear. They narrow it if
        // they know; the chips are right there.
        context: 'any' as PlanContext,
        directionRef: direction?.directionId ?? null,
        saidAloud: false,
      };
    });
  }

  function addPlan() {
    if (draft.plans.length >= ONBOARDING_LIMITS.maxPlans) return;
    draft.plans = [
      ...draft.plans,
      {
        planId: newPlanId(),
        trigger: '',
        action: '',
        context: 'any',
        directionRef: draft.directions[0]?.directionId ?? null,
        saidAloud: false,
      },
    ];
  }

  function removePlan(planId: string) {
    draft.plans = draft.plans.filter((p) => p.planId !== planId);
  }

  const completePlans = $derived(
    draft.plans.filter((p) => p.trigger.trim().length > 0 && p.action.trim().length > 0),
  );
  const plansReady = $derived(completePlans.length >= ONBOARDING_LIMITS.minPlans);

  // ---------------------------------------------------------------- done
  async function finish() {
    // Single-flight. A double tap would otherwise start two commits and write
    // the plans and the completion event twice — and the log keeps both.
    if (finishing) return;
    finishing = true;
    failed = false;
    try {
      const ctx = await app.context();
      // Already onboarded — a cleared first-run flag, or two tabs. Nothing to
      // write; just go where they were headed.
      if (app.derived.onboarding.completed) {
        navigate('catch');
        return;
      }
      await completeOnboarding(
        ctx,
        { ...$state.snapshot(draft), plans: $state.snapshot(completePlans), step: 'done' },
        saidAloud === undefined ? {} : { saidAloud },
      );
      await app.refresh();
      // Architecture §2.3: ask the browser not to evict our storage, *after*
      // onboarding rather than at first launch — the prompt some browsers show
      // is meaningless before the user has anything to lose.
      void requestPersistentStorage();
      navigate('catch');
    } catch {
      finishing = false;
      failed = true;
    }
  }

  async function leave() {
    // The draft is kept, so returning resumes exactly here. Nothing is written
    // to the log: a half-filled form is not a fact about someone's life.
    await persist();
    navigate('catch');
  }

  async function startOver() {
    const ctx = await app.context();
    await clearDraft(ctx);
    draft = emptyDraft();
    step = 'intro';
  }
  void startOver;

  const aloudPlan = $derived<DraftPlan | undefined>(completePlans[aloudIndex]);
</script>

{#if loaded}
  <main>
    {#if step === 'intro'}
      <h1 tabindex="-1" use:focusOnMount>{t('onboard.intro.title')}</h1>
      <p class="body">{t('onboard.intro.body')}</p>
      <p class="note">{t('onboard.intro.note')}</p>
      <button type="button" class="primary" onclick={() => go('directions')}>
        {t('onboard.intro.start')}
      </button>

    {:else if step === 'directions'}
      <h1 tabindex="-1" use:focusOnMount>{t('onboard.directions.title')}</h1>
      <p class="help">{t('onboard.directions.help')}</p>

      <div class="chips">
        {#each SUGGESTED_DIRECTIONS as suggestion (suggestion.directionId)}
          {@const label = t(suggestion.labelKey)}
          {@const selected = draft.directions.some((d) => d.directionId === suggestion.directionId)}
          <Chip
            {selected}
            disabled={!selected && directionCount >= ONBOARDING_LIMITS.maxDirections}
            onclick={() => toggleDirection(suggestion.directionId, label)}
          >
            {label}
          </Chip>
        {/each}
      </div>

      {#if ownDirections.length > 0}
        <div class="chips">
          {#each ownDirections as own (own.directionId)}
            <Chip selected onclick={() => toggleDirection(own.directionId, own.label)}>
              {own.label}
            </Chip>
          {/each}
        </div>
      {/if}

      <div class="row">
        <input
          type="text"
          bind:value={customDirection}
          placeholder={t('onboard.direction.customPlaceholder')}
          aria-label={t('onboard.direction.custom')}
          maxlength="80"
        />
        <button type="button" class="add" aria-label={t('onboard.direction.add')} onclick={addCustomDirection}>+</button>
      </div>

      <div class="nav">
        <button type="button" class="back" onclick={() => go(previousStep(step))}>
          {t('tag.back')}
        </button>
        <button
          type="button"
          class="primary"
          disabled={directionCount === 0}
          onclick={() => go('expressions')}
        >
          {t('onboard.intro.start')}
        </button>
      </div>

    {:else if step === 'expressions'}
      <h1 tabindex="-1" use:focusOnMount>{t('onboard.expressions.title')}</h1>
      <p class="help">{t('onboard.expressions.help')}</p>

      {#each draft.directions as direction (direction.directionId)}
        <label class="field">
          <span class="field-label">{direction.label}</span>
          <input
            type="text"
            bind:value={draft.expressions[direction.directionId]}
            placeholder={t('onboard.expressions.placeholder')}
            maxlength="120"
          />
        </label>
      {/each}

      <div class="nav">
        <button type="button" class="back" onclick={() => go(previousStep(step))}>
          {t('tag.back')}
        </button>
        <button type="button" class="primary" onclick={() => go('behaviors')}>
          {t('onboard.intro.start')}
        </button>
      </div>

    {:else if step === 'behaviors'}
      <h1 tabindex="-1" use:focusOnMount>{t('onboard.behaviors.title')}</h1>
      <p class="help">{t('onboard.behaviors.help')}</p>

      <div class="chips">
        {#each SUGGESTED_BEHAVIORS as suggestion (suggestion.behaviorId)}
          {@const label = t(suggestion.labelKey)}
          {@const selected = draft.targetBehaviors.some((b) => b.behaviorId === suggestion.behaviorId)}
          <Chip
            {selected}
            disabled={!selected &&
              draft.targetBehaviors.length >= ONBOARDING_LIMITS.maxTargetBehaviors}
            onclick={() => toggleBehavior(suggestion.behaviorId, label)}
          >
            {label}
          </Chip>
        {/each}
      </div>

      {#if ownBehaviors.length > 0}
        <div class="chips">
          {#each ownBehaviors as own (own.behaviorId)}
            <Chip selected onclick={() => toggleBehavior(own.behaviorId, own.label)}>
              {own.label}
            </Chip>
          {/each}
        </div>
      {/if}

      <div class="row">
        <input
          type="text"
          bind:value={customBehavior}
          placeholder={t('onboard.behavior.customPlaceholder')}
          aria-label={t('onboard.behavior.custom')}
          maxlength="80"
        />
        <button type="button" class="add" aria-label={t('onboard.behavior.add')} onclick={addCustomBehavior}>+</button>
      </div>

      <div class="nav">
        <button type="button" class="back" onclick={() => go(previousStep(step))}>
          {t('tag.back')}
        </button>
        <button
          type="button"
          class="primary"
          disabled={draft.targetBehaviors.length === 0}
          onclick={() => {
            seedPlans();
            void go('plans');
          }}
        >
          {t('onboard.intro.start')}
        </button>
      </div>

    {:else if step === 'plans'}
      <h1 tabindex="-1" use:focusOnMount>{t('onboard.plans.title')}</h1>
      <p class="help">{t('onboard.plans.help')}</p>

      {#each draft.plans as plan (plan.planId)}
        <div class="plan-card">
          <label class="field">
            <span class="field-label">{t('onboard.plans.when')}</span>
            <input
              type="text"
              bind:value={plan.trigger}
              placeholder={t('onboard.plans.triggerPlaceholder')}
              maxlength="120"
            />
          </label>
          <label class="field">
            <span class="field-label">{t('onboard.plans.then')}</span>
            <input
              type="text"
              bind:value={plan.action}
              placeholder={t('onboard.plans.actionPlaceholder')}
              maxlength="120"
            />
          </label>
          <div class="chips small">
            {#each PLAN_CONTEXTS as context (context)}
              <Chip selected={plan.context === context} onclick={() => (plan.context = context)}>
                {t(contextLabelKey(context))}
              </Chip>
            {/each}
          </div>
          {#if draft.plans.length > ONBOARDING_LIMITS.minPlans}
            <button type="button" class="back" onclick={() => removePlan(plan.planId)}>
              {t('onboard.plans.remove')}
            </button>
          {/if}
        </div>
      {/each}

      <p class="note">
        {t('onboard.plans.count', {
          done: completePlans.length,
          min: ONBOARDING_LIMITS.minPlans,
        })}
      </p>

      {#if draft.plans.length < ONBOARDING_LIMITS.maxPlans}
        <button type="button" class="back" onclick={addPlan}>{t('onboard.plans.add')}</button>
      {/if}

      <div class="nav">
        <button type="button" class="back" onclick={() => go(previousStep(step))}>
          {t('tag.back')}
        </button>
        <button type="button" class="primary" disabled={!plansReady} onclick={() => go('aloud')}>
          {t('onboard.intro.start')}
        </button>
      </div>

    {:else if step === 'aloud'}
      <h1 tabindex="-1" use:focusOnMount>{t('onboard.aloud.title')}</h1>
      {#if aloudPlan}
        <p class="spoken">
          {t('onboard.plans.when')}
          {aloudPlan.trigger}, {t('onboard.plans.then')}
          {aloudPlan.action}.
        </p>
      {/if}
      <p class="help">{t('onboard.aloud.help')}</p>

      <div class="nav">
        <button
          type="button"
          class="back"
          onclick={() => {
            saidAloud = saidAloud ?? false;
            if (aloudIndex + 1 < completePlans.length) aloudIndex += 1;
            else void go('placement');
          }}
        >
          {t('onboard.aloud.skip')}
        </button>
        <button
          type="button"
          class="primary"
          onclick={() => {
            saidAloud = true;
            if (aloudIndex + 1 < completePlans.length) aloudIndex += 1;
            else void go('placement');
          }}
        >
          {t('onboard.aloud.done')}
        </button>
      </div>

    {:else if step === 'placement'}
      <PlacementRitual
        onDone={() => {
          draft.placementRitualDone = true;
          void go('arc');
        }}
        onLater={() => {
          draft.placementRitualDone = false;
          void go('arc');
        }}
      />

    {:else if step === 'arc'}
      <h1 tabindex="-1" use:focusOnMount>{t('onboard.arc.title')}</h1>
      <ol class="arc">
        <li>{t('onboard.arc.phase1')}</li>
        <li>{t('onboard.arc.phase2')}</li>
        <li>{t('onboard.arc.phase3')}</li>
      </ol>
      <p class="body">{t('onboard.arc.point')}</p>
      <!-- Spec §14: an early rise in Catches must not read as getting worse.
           Onboarding is where that expectation is set, before there is any data
           to misread. -->
      <p class="note">{t('onboard.arc.earlyRise')}</p>

      <button type="button" class="primary" disabled={finishing} onclick={() => void finish()}>
        {t('onboard.arc.finish')}
      </button>
      <p class="failed" role="alert">{failed ? t('error.notSaved') : ''}</p>

    {:else}
      <h1 tabindex="-1" use:focusOnMount>{t('onboard.done.title')}</h1>
      <p class="body">{t('onboard.done.body')}</p>
      <button type="button" class="primary" onclick={() => navigate('catch')}>
        {t('onboard.done.go')}
      </button>
    {/if}

    {#if step !== 'done'}
      <button type="button" class="leave" onclick={() => void leave()}>{t('onboard.leave')}</button>
    {/if}
  </main>
{/if}

<style>
  main {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-6) var(--space-5) var(--space-6);
    overflow-y: auto;
  }

  h1 {
    font-size: var(--step-2);
    font-weight: 400;
    line-height: 1.25;
  }

  .body {
    font-size: var(--step-0);
    color: var(--c-ink);
  }

  .help,
  .note {
    font-size: var(--step--1);
    color: var(--c-ink-muted);
  }

  .note {
    font-style: italic;
  }

  .spoken {
    font-size: var(--step-1);
    color: var(--c-accent);
    line-height: 1.4;
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .chips.small {
    gap: var(--space-1);
  }

  .row {
    display: flex;
    gap: var(--space-2);
    align-items: stretch;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .field-label {
    font-size: var(--step--1);
    color: var(--c-ink-muted);
  }

  input {
    flex: 1;
    min-height: var(--tap-min);
    padding: var(--space-3);
    border-radius: var(--radius);
    border: 1px solid var(--c-line);
    background: var(--c-surface);
    color: var(--c-ink);
    font: inherit;
    font-size: var(--step-0);
  }

  input::placeholder {
    color: var(--c-ink-faint);
  }

  .plan-card {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-4);
    border: 1px solid var(--c-line);
    border-radius: var(--radius);
  }

  .arc {
    margin: 0;
    padding-left: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    color: var(--c-ink-muted);
  }

  .nav {
    display: flex;
    gap: var(--space-3);
    align-items: center;
    justify-content: space-between;
    margin-top: auto;
    padding-top: var(--space-4);
  }

  .primary {
    padding: var(--space-3) var(--space-6);
    border-radius: var(--radius-lg);
    border: 1px solid var(--c-accent-line);
    color: var(--c-accent);
    font-size: var(--step-1);
  }

  .primary:disabled {
    opacity: 0.4;
  }

  .add {
    min-width: var(--tap-min);
    border-radius: var(--radius);
    border: 1px solid var(--c-line);
    color: var(--c-ink-muted);
    font-size: var(--step-1);
  }

  .back,
  .leave {
    color: var(--c-ink-faint);
    font-size: var(--step--1);
    padding: var(--space-2) var(--space-3);
    align-self: flex-start;
  }

  .leave {
    align-self: center;
    margin-top: var(--space-3);
  }

  .failed {
    min-height: 1.5em;
    color: var(--c-ink-muted);
    font-size: var(--step--1);
  }
</style>
